import { TrackBroadcast } from "./objects";
/**
 * A handler to allow for the invoker to receive live updates on when tracks are added.
 */
var TrackReceiver = /** @class */ (function () {
    /**
     * Creates a `TrackReceiver` object to handle receiving tracks when they are added.
     *
     * @param websocketURL The URL to the websocket connecting to the backend. If not present, a websocket will never
     *                     be established.
     * @param underground If `true`, only tracks on the underground station will be broadcasted. If `false`, only FM
     *                    tracks will be sent.
     * @param receiveTrack The handler to be invoked when a track is received
     */
    function TrackReceiver(websocketURL, underground, receiveTrack) {
        this.websocketURL = websocketURL;
        this.underground = underground;
        this.receiveTrack = receiveTrack;
    }
    /**
     * Connects to the websocket. If `websocketUrl` is undefined or {@link webSocket} is defined, this will do nothing.
     *
     * @return A promise of the open status (`true` indicated connected, false if no `websocketURL` is present)
     */
    TrackReceiver.prototype.connectWebsocket = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (_this.websocketURL == undefined || _this.webSocket != undefined) {
                resolve(false);
                return;
            }
            try {
                var urlQuery = new URLSearchParams({ underground: "".concat(_this.underground) });
                _this.webSocket = new WebSocket("".concat(_this.websocketURL, "/api/tracks/stream?").concat(urlQuery));
                _this.webSocket.onmessage = function (event) {
                    _this.receiveTrack(TrackBroadcast.fromJSON(JSON.parse(event.data)));
                };
                _this.webSocket.onerror = function (error) {
                    console.error(error);
                    reject();
                };
                _this.webSocket.onopen = function (_) {
                    console.debug('Connected to websocket!');
                    resolve(true);
                };
                // Heartbeat every 50 seconds
                _this.heartbeatInterval = setInterval(function () {
                    if (_this.webSocket != undefined) {
                        _this.heartbeat(_this.webSocket);
                    }
                }, 50000);
                _this.webSocket.onclose = function (_) {
                    setTimeout(function () {
                        _this.connectWebsocket();
                    }, 3000);
                };
            }
            catch (e) {
                console.error(e);
                reject();
            }
        });
    };
    /**
     * Sends a heartbeat message to the websocket, if it is open.
     *
     * @param websocket The websocket to send the heartbeat to
     */
    TrackReceiver.prototype.heartbeat = function (websocket) {
        if (websocket.readyState == WebSocket.OPEN) {
            websocket.send(JSON.stringify({ 'heartbeat': '' }));
        }
    };
    /**
     * If the websocket is open, the currently playing track is requested. This will be sent to {@link receiveTrack}.
     *
     * @return If the track could be requested. This gives no indication if the track was actually received, just if it
     *         was requested
     */
    TrackReceiver.prototype.requestCurrentTrack = function () {
        if (this.webSocket == undefined || this.webSocket.readyState != WebSocket.OPEN) {
            return false;
        }
        this.webSocket.send(JSON.stringify({ 'request': 'current' }));
        return true;
    };
    /**
     * Sets the receiver to listen to underground or FM. An update will send the currently playing track on the stream.
     *
     * @param underground `true` if underground should be listened to
     * @return The {@link connectWebsocket} status of the new websocket
     */
    TrackReceiver.prototype.setUnderground = function (underground) {
        var _a;
        if (this.underground == underground) {
            return Promise.resolve(false);
        }
        this.underground = underground;
        clearInterval(this.heartbeatInterval);
        (_a = this.webSocket) === null || _a === void 0 ? void 0 : _a.close();
        this.webSocket = undefined;
        return this.connectWebsocket();
    };
    return TrackReceiver;
}());
export { TrackReceiver };
